import { getData, postData, putData, patchData } from '../services/FetchNodeServices.js';

export interface EInvoiceRecord {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  irn: string;
  ackNumber: string;
  ackDate: string;
  signedInvoiceJson: string;
  qrCodeData: string;
  status: 'GENERATED' | 'CANCELLED' | 'PENDING' | 'FAILED';
  irpStatus?: 'ACT' | 'CNL' | null;
  ewbNo?: string | null;
  ewbDate?: string | null;
  ewbValidTill?: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  irpResponse: string;
  generatedBy: string;
  createdAt: string;
}

export interface EInvoiceGstinDetails {
  gstin: string | null;
  legalName: string | null;
  tradeName: string | null;
  address: string | null;
  pincode: number | string | null;
  stateCode: string | null;
  taxpayerType: string | null;
  taxpayerTypeLabel: string;
  status: string | null;
  isActive: boolean;
  blockStatus: string | null;
  isBlocked: boolean;
  registeredOn: string | null;
  deregisteredOn: string | null;
}

export interface EInvoiceIRNDetails {
  ackNo: number | string | null;
  ackDate: string | null;
  irn: string;
  status: 'ACT' | 'CNL' | string | null;
  ewbNo: string | number | null;
  ewbDate: string | null;
  ewbValidTill: string | null;
  remarks: string | null;
  signedInvoice: string | null;
  signedQRCode: string | null;
  decodedInvoice: any | null;
  local: { status: string; irpStatus: string | null } | null;
}

export interface EWayBillItem {
  itemId: string;
  itemName: string;
  hsnCode: string;
  qty: number;
  unit: string;
  taxableValue: number;
  taxRate: number;
}

export interface EWayBillRecord {
  id: string;
  ewbNumber: string;
  invoiceId: string | null;
  challanId: string | null;
  invoiceNumber: string;
  customerName: string;
  supplierName: string;
  generatedDate: string;
  validUntil: string;
  validityDays: number;
  distanceKm: number;
  fromGSTIN: string;
  toGSTIN: string;
  fromAddress: string;
  toAddress: string;
  transporterId: string | null;
  transporterName: string | null;
  vehicleNumber: string | null;
  vehicleType: 'ROAD' | 'RAIL' | 'AIR' | 'SHIP';
  transportMode: '1' | '2' | '3' | '4';
  totalValue: number;
  totalTaxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  status: 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'EXTENDED';
  cancelledAt: string | null;
  cancelReason: string | null;
  generatedBy: string;
  documentType?: string;
  docType?: string;
  items: EWayBillItem[];
}

export interface TDSEntryRecord {
  id: string;
  type: 'TDS' | 'TCS' | 'TDS_RECEIVABLE';
  section: string;
  partyId: string;
  partyName: string;
  partyPAN: string;
  partyType: 'INDIVIDUAL' | 'COMPANY' | 'HUF' | 'FIRM' | 'CUSTOMER';
  invoiceId: string | null;
  invoiceNumber: string | null;
  paymentDate: string;
  paymentAmount: number;
  taxableAmount: number;
  tdsRate: number;
  tdsAmount: number;
  tdsChallanNumber: string | null;
  depositDate: string | null;
  status: 'DEDUCTED' | 'DEPOSITED' | 'PENDING' | 'EXEMPTED' | 'EXPECTED' | 'CERTIFICATE_PENDING' | 'RECEIVED' | 'RECONCILED' | 'CLAIMED';
  quarterlyReturn: string | null;
  bankBSRCode?: string | null;
  bankName?: string | null;
  exemptionReason?: string | null;
  challanGroupId?: string | null;
  certificateNo?: string | null;
  financialYear?: string | null;
  quarter?: string | null;
  createdAt: string;
}

export interface TDSSectionRecord {
  id: string;
  section: string;
  description: string;
  threshold: number;
  annualThreshold: number;
  rateIndividual: number;
  rateCompany: number;
  isActive: boolean;
}

export interface TdsTcsSettings {
  clientId: string;
  clientSecret: string;
  gstUsername: string;
  gstin: string;
  stateCd: string;
  environment: 'sandbox' | 'production';
  email: string;
  serverIp: string;
  tan: string;
  enableTcs: boolean;
  tcsThreshold: number;
  tcsRate: number;
  tcsRateWithoutPan: number;
  autoTdsOnPayments: boolean;
  autoTcsOnSales: boolean;
  remindDays: number;
  emailAlertsEnabled: boolean;
  emailRecipients: string;
  updatedAt?: string | null;
  session: {
    status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'LOGGED_OUT';
    active: boolean;
    expiresAt: string | null;
  };
}

export interface CustomerAnnualSales {
  id?: string;
  customerId: string;
  customerName?: string;
  financialYear: string;
  totalTaxableAmount: number;
  totalTcsCollected: number;
  invoiceCount: number;
  lastInvoiceDate: string | null;
  tcsThresholdCrossed: boolean;
  thresholdCrossedOn: string | null;
  updatedAt?: string;
}

export interface GstinVerifyResult {
  gstin: string;
  status: string | null;
  isActive: boolean;
  tradeName: string | null;
  legalName: string | null;
  stateCode: string | null;
  state: string | null;
  registrationDate: string | null;
  taxpayerType: string | null;
  cancelledDate: string | null;
}

export interface ChallanGroupRecord {
  id: string;
  challanNumber: string;
  bsrCode: string;
  bankName: string;
  depositDate: string;
  quarter: string;
  financialYear: string;
  entryIds: string[];
  totalAmount: number;
  status: string;
  createdAt: string;
  interestAmount?: number;
  totalPayable?: number;
  periodMonth?: number | null;
  periodYear?: number | null;
  remarks?: string | null;
  challanType?: 'TDS' | 'TCS';
}

export interface ChallanSectionBreakup {
  section: string;
  totalTaxable: number;
  totalTDS: number;
  entries: Array<{ id: string; partyName: string; taxableAmount: number; tdsRate: number; tdsAmount: number }>;
}

export interface ChallanPreview {
  periodMonth: number;
  periodYear: number;
  type: 'TDS' | 'TCS';
  dueDate: string;
  isLate: boolean;
  monthsLate: number;
  sectionWise: ChallanSectionBreakup[];
  totalTDS: number;
  interest: number;
  totalPayable: number;
  formType: string;
  companyTan: string | null;
  companyPan: string | null;
  companyName: string | null;
  assessmentYear: string;
  entryIds: string[];
}

export interface ChallanSummary {
  totalDeposited: number;
  totalPending: number;
  pendingByMonth: Array<{ month: number; year: number; amount: number }>;
  nextDue: { date: string; month: number; year: number; amount: number } | null;
}

export interface Gstr2xReconciledEntry {
  id: string;
  partyId: string | null;
  partyName: string;
  partyPan: string | null;
  section: string | null;
  tdsAmount: number;
  status: string;
  gstnAmount?: number;
  difference?: number;
  note?: string;
}

export interface Gstr2xReconciliationResult {
  matched: Gstr2xReconciledEntry[];
  mismatched: Gstr2xReconciledEntry[];
  missing: Gstr2xReconciledEntry[];
  totalEntries: number;
  totalGovtCredits: number;
  totalGSTNAmount: number;
  totalOurAmount: number;
  reconciledAt: string;
}

export interface GstReturnFiling {
  id: string;
  company_id: string;
  gstin: string;
  ret_period: string;
  financial_year: string;
  total_tds_amount?: number;
  total_tcs_amount?: number;
  total_taxable_amount: number;
  ref_id: string | null;
  arn: string | null;
  status: 'DRAFT' | 'SAVED' | 'PROCESSING' | 'ACCEPTED' | 'FILED' | 'ERROR';
  error_details: string | null;
  filed_at: string | null;
  created_at: string;
}

// ── TDS Receivable (Stage 4 — Form 16A certificate tracking) ──
export type TdsReceivableStatus = 'EXPECTED' | 'CERTIFICATE_PENDING' | 'RECEIVED' | 'RECONCILED' | 'CLAIMED' | 'OVERDUE';

export interface TdsReceivableEntry {
  id: string;
  section: string | null;
  partyId: string | null;
  partyName: string;
  partyPAN: string | null;
  partyTan?: string | null;
  partyEmail?: string | null;
  invoiceId: string;
  invoiceNumber: string;
  paymentId: string | null;
  paymentDate: string | null;
  tdsRate: number | null;
  tdsAmount: number;
  financialYear: string;
  quarter: string;
  status: Exclude<TdsReceivableStatus, 'OVERDUE'>;
  displayStatus: TdsReceivableStatus;
  certificateNo: string | null;
  certificateDate: string | null;
  certTdsAmount: number | null;
  challanNo: string | null;
  bsrCode: string | null;
  certificateFileUrl: string | null;
  certificateRemarks: string | null;
  hasAmountMismatch: boolean;
  amountMismatchValue: number;
  receivedAt: string | null;
  reminderSentAt: string | null;
  createdAt: string;
  isVirtual: boolean;
}

export interface TdsReceivableSummary {
  financialYear: string;
  fyTotalTds: number;
  certPendingCount: number;
  certPendingAmount: number;
  receivedAmount: number;
  nextDue: { date: string; quarter: string; financialYear: string } | null;
}

export interface TdsReminderEmail {
  to: string;
  subject: string;
  body: string;
}

export const complianceApi = {
    // ─── Add these 2 functions to your compliance.api.ts ─────────────────────────
// Inside the complianceApi object, add after searchChallans:

  getInvoiceById: async (id: string) => {
    const res = await getData(`api/v1/sales-invoices/${id}`);
    return res;
  },

  getChallanById: async (id: string) => {
    const res = await getData(`api/v1/challans/${id}`);
    return res;
  },
  // ── E-Invoice ──
  getEInvoices: async (params?: { status?: string; search?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    const res = await getData(`api/v1/compliance/e-invoice${q ? `?${q}` : ''}`);
    return res;
  },
  generateEInvoice: async (payload: { invoiceId?: string; invoiceNumber?: string }) => {
    const res = await postData(`api/v1/compliance/e-invoice/generate`, payload);
    return res;
  },
  cancelEInvoice: async (payload: { id: string; cancelReason: string }) => {
    const res = await postData(`api/v1/compliance/e-invoice/cancel`, payload);
    return res;
  },
  retryEInvoice: async (payload: { id: string }) => {
    const res = await postData(`api/v1/compliance/e-invoice/retry`, payload);
    return res;
  },

  // ── E-Way Bill ──
  getEWayBills: async (params?: { status?: string; search?: string; dateFrom?: string; dateTo?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    const res = await getData(`api/v1/compliance/eway-bill${q ? `?${q}` : ''}`);
    return res;
  },
  generateEWayBill: async (payload: any) => {
    const res = await postData(`api/v1/compliance/eway-bill/generate`, payload);
    return res;
  },
  extendEWayBill: async (payload: {
    ewbId?: string;
    ewbNo: string | number;
    vehicleNo?: string;
    fromPlace: string;
    fromState: number | string;
    fromPincode: number | string;
    remainingDistance: number | string;
    transMode?: string;
    transDocNo?: string;
    transDocDate?: string;
    extnRsnCode: number | string;
    extnRemarks: string;
    consignmentStatus?: string;
    transitType?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
  }) => {
    const res = await postData(`api/v1/compliance/eway-bill/extend`, payload);
    return res;
  },
  cancelEWayBill: async (payload: {
    ewbId?: string;
    ewbNo: string | number;
    cancelRsnCode: number | string;
    cancelRmrk?: string;
  }) => {
    const res = await postData(`api/v1/compliance/eway-bill/cancel`, payload);
    return res;
  },
  updateVehicle: async (payload: {
    ewbId?: string;
    ewbNo: string | number;
    vehicleNo?: string;
    fromPlace: string;
    fromState: number | string;
    reasonCode: string | number;
    reasonRem: string;
    transMode?: string;
    transDocNo?: string;
    transDocDate?: string;
  }) => {
    const res = await putData(`api/v1/compliance/eway-bill/update-vehicle`, payload);
    return res;
  },
  updateTransporter: async (payload: {
    ewbId?: string;
    ewbNo: string | number;
    transporterId: string;
    transporterName?: string;
  }) => {
    const res = await postData(`api/v1/compliance/eway-bill/update-transporter`, payload);
    return res;
  },
  getEWayBillDetails: async (ewbNo: string | number, supplierGstin?: string) => {
    const params = new URLSearchParams();
    params.set('ewbNo', String(ewbNo));
    if (supplierGstin) params.set('supplierGstin', supplierGstin);
    const res = await getData(`api/v1/compliance/eway-bill/details?${params.toString()}`);
    return res;
  },

  // ── TDS / TCS ──
  getTDSEntries: async (params?: { type?: string; section?: string; status?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    const res = await getData(`api/v1/compliance/tds/entries${q ? `?${q}` : ''}`);
    return res;
  },
  createTDSEntry: async (payload: any) => {
    const res = await postData(`api/v1/compliance/tds/entries`, payload);
    return res;
  },
  getTDSSections: async () => {
    const res = await getData(`api/v1/compliance/tds/sections`);
    return res;
  },
  recordTDSChallanDeposit: async (payload: any) => {
    const res = await postData(`api/v1/compliance/tds/deposit`, payload);
    return res;
  },
  getChallanGroups: async (type?: 'TDS' | 'TCS'): Promise<{ success: boolean; message?: string; data?: ChallanGroupRecord[] }> => {
    const res = await getData(`api/v1/compliance/tds/challan-groups${type ? `?type=${type}` : ''}`);
    return res;
  },
  getChallanPreview: async (month: number, year: number, type: 'TDS' | 'TCS' = 'TDS'): Promise<{ success: boolean; message?: string; data?: ChallanPreview }> => {
    const res = await getData(`api/v1/compliance/tds/challan-preview?month=${month}&year=${year}&type=${type}`);
    return res;
  },
  getChallanSummary: async (type: 'TDS' | 'TCS' = 'TDS'): Promise<{ success: boolean; message?: string; data?: ChallanSummary }> => {
    const res = await getData(`api/v1/compliance/tds/challan-summary?type=${type}`);
    return res;
  },
  updateTDSSection: async (
    id: string,
    changes: boolean | Partial<Pick<TDSSectionRecord, 'isActive' | 'description' | 'threshold' | 'annualThreshold' | 'rateIndividual' | 'rateCompany'>>
  ): Promise<{ success: boolean; message?: string; data?: TDSSectionRecord }> => {
    const body = typeof changes === 'boolean' ? { isActive: changes } : changes;
    const res = await putData(`api/v1/compliance/tds/sections/${id}`, body);
    return res;
  },

  // ── TDS/TCS Whitebooks GST Return Filing Settings ──
  getTdsTcsSettings: async (): Promise<{ success: boolean; message?: string; data?: TdsTcsSettings }> => {
    const res = await getData(`api/v1/compliance/tds/settings`);
    return res;
  },
  updateTdsTcsSettings: async (
    payload: Partial<Omit<TdsTcsSettings, 'session' | 'updatedAt'>>
  ): Promise<{ success: boolean; message?: string; errors?: string[]; data?: TdsTcsSettings }> => {
    const res = await putData(`api/v1/compliance/tds/settings`, payload);
    return res;
  },

  // ── TCS threshold tracking (Section 206C(1H)) ──
  getCustomerAnnualSales: async (params: { customerId?: string; financialYear?: string; applicableOnly?: boolean } = {}): Promise<{
    success: boolean; message?: string; data?: CustomerAnnualSales | CustomerAnnualSales[];
  }> => {
    const query = new URLSearchParams();
    if (params.customerId) query.set('customerId', params.customerId);
    if (params.financialYear) query.set('financialYear', params.financialYear);
    if (params.applicableOnly) query.set('applicableOnly', 'true');
    const qs = query.toString();
    const res = await getData(`api/v1/compliance/tds/tcs/customer-annual-sales${qs ? `?${qs}` : ''}`);
    return res;
  },

  // ── OTP Authentication (PDF §2) ──
  requestGstnOtp: async (): Promise<{ success: boolean; message?: string }> => {
    const res = await postData(`api/v1/compliance/tds/gstn/otp-request`, {});
    return res;
  },
  verifyGstnOtp: async (otp: string): Promise<{ success: boolean; message?: string; expiresAt?: string }> => {
    const res = await postData(`api/v1/compliance/tds/gstn/auth-token`, { otp });
    return res;
  },
  refreshGstnToken: async (): Promise<{ success: boolean; message?: string; expiresAt?: string }> => {
    const res = await postData(`api/v1/compliance/tds/gstn/refresh-token`, {});
    return res;
  },
  logoutGstn: async (): Promise<{ success: boolean; message?: string }> => {
    const res = await postData(`api/v1/compliance/tds/gstn/logout`, {});
    return res;
  },

  // ── GSTIN verification (public, no session token needed) ──
  verifyTdsGstin: async (
    gstin: string,
    partyId?: string
  ): Promise<{ success: boolean; message?: string; authRequired?: boolean; notFound?: boolean; data?: GstinVerifyResult }> => {
    const res = await postData(`api/v1/compliance/tds/gstn/verify-gstin`, { gstin: gstin.trim().toUpperCase(), partyId });
    return res;
  },
  trackCustomerReturns: async (gstin: string, fy?: string) => {
    const params = new URLSearchParams({ gstin });
    if (fy) params.set('fy', fy);
    const res = await getData(`api/v1/compliance/tds/gstn/return-track?${params.toString()}`);
    return res;
  },

  // ── GSTR7 — TDS return ──
  saveGSTR7: async (retPeriod: string, financialYear?: string): Promise<{ success: boolean; message?: string; authRequired?: boolean; data?: GstReturnFiling }> => {
    const res = await postData(`api/v1/compliance/tds/gstr7/save`, { retPeriod, financialYear });
    return res;
  },
  getGSTR7Status: async (retPeriod: string) => {
    const res = await getData(`api/v1/compliance/tds/gstr7/status?retPeriod=${encodeURIComponent(retPeriod)}`);
    return res;
  },
  fileGSTR7: async (retPeriod: string): Promise<{ success: boolean; message?: string; authRequired?: boolean; data?: GstReturnFiling }> => {
    const res = await postData(`api/v1/compliance/tds/gstr7/file`, { retPeriod });
    return res;
  },
  getGSTR7Summary: async (retPeriod: string) => {
    const res = await getData(`api/v1/compliance/tds/gstr7/summary?retPeriod=${encodeURIComponent(retPeriod)}`);
    return res;
  },

  // ── GSTR8 — TCS return ──
  saveGSTR8: async (retPeriod: string, financialYear?: string): Promise<{ success: boolean; message?: string; authRequired?: boolean; data?: GstReturnFiling }> => {
    const res = await postData(`api/v1/compliance/tds/gstr8/save`, { retPeriod, financialYear });
    return res;
  },
  getGSTR8Status: async (retPeriod: string) => {
    const res = await getData(`api/v1/compliance/tds/gstr8/status?retPeriod=${encodeURIComponent(retPeriod)}`);
    return res;
  },
  fileGSTR8: async (retPeriod: string): Promise<{ success: boolean; message?: string; authRequired?: boolean; data?: GstReturnFiling }> => {
    const res = await postData(`api/v1/compliance/tds/gstr8/file`, { retPeriod });
    return res;
  },
  getGSTR8Summary: async (retPeriod: string) => {
    const res = await getData(`api/v1/compliance/tds/gstr8/summary?retPeriod=${encodeURIComponent(retPeriod)}`);
    return res;
  },

  // ── GSTR2X reconciliation ──
  reconcileGSTR2X: async (retPeriod: string): Promise<{ success: boolean; message?: string; authRequired?: boolean; data?: Gstr2xReconciliationResult }> => {
    const res = await postData(`api/v1/compliance/tds/gstr2x/reconcile`, { retPeriod });
    return res;
  },

  // ── Ledger balances ──
  getItcLedger: async (gstin?: string) => {
    const res = await getData(`api/v1/compliance/tds/ledger/itc${gstin ? `?gstin=${encodeURIComponent(gstin)}` : ''}`);
    return res;
  },
  getCashLedger: async (gstin?: string) => {
    const res = await getData(`api/v1/compliance/tds/ledger/cash${gstin ? `?gstin=${encodeURIComponent(gstin)}` : ''}`);
    return res;
  },

  // ── Local filing status (dashboard cards) ──
  getGstFilings: async (type: 'GSTR7' | 'GSTR8', period?: string): Promise<{ success: boolean; message?: string; data?: GstReturnFiling | GstReturnFiling[] | null }> => {
    const params = new URLSearchParams({ type });
    if (period) params.set('period', period);
    const res = await getData(`api/v1/compliance/tds/filings?${params.toString()}`);
    return res;
  },

  // ── TDS Receivable — Form 16A certificate tracking ──
  getTdsReceivable: async (params?: {
    financialYear?: string;
    quarter?: string;
    status?: string;
    search?: string;
  }): Promise<{ success: boolean; message?: string; data?: TdsReceivableEntry[] }> => {
    const q = new URLSearchParams();
    if (params?.financialYear) q.set('financialYear', params.financialYear);
    if (params?.quarter) q.set('quarter', params.quarter);
    if (params?.status) q.set('status', params.status);
    if (params?.search) q.set('search', params.search);
    const res = await getData(`api/v1/compliance/tds/receivable${q.toString() ? `?${q.toString()}` : ''}`);
    return res;
  },
  getTdsReceivableSummary: async (financialYear?: string): Promise<{ success: boolean; message?: string; data?: TdsReceivableSummary }> => {
    const q = financialYear ? `?financialYear=${encodeURIComponent(financialYear)}` : '';
    const res = await getData(`api/v1/compliance/tds/receivable/summary${q}`);
    return res;
  },
  saveTdsCertificate: async (id: string, formData: FormData): Promise<{ success: boolean; message?: string; data?: TdsReceivableEntry; warnings?: string[] }> => {
    const res = await putData(`api/v1/compliance/tds/receivable/${id}/certificate`, formData);
    return res;
  },
  markTdsClaimed: async (id: string): Promise<{ success: boolean; message?: string; data?: { id: string; status: string } }> => {
    const res = await patchData(`api/v1/compliance/tds/receivable/${id}/status`, { status: 'CLAIMED' });
    return res;
  },
  getTdsReminderEmail: async (id: string): Promise<{ success: boolean; message?: string; data?: TdsReminderEmail }> => {
    const res = await postData(`api/v1/compliance/tds/receivable/${id}/reminder`, {});
    return res;
  },
  getTdsBulkReminderEmails: async (
    financialYear: string,
    quarter: string
  ): Promise<{ success: boolean; message?: string; data?: { emails: (TdsReminderEmail & { entryId: string })[]; totalPending: number; count: number } }> => {
    const q = new URLSearchParams({ financialYear, quarter });
    const res = await postData(`api/v1/compliance/tds/receivable/reminder/bulk?${q.toString()}`, {});
    return res;
  },

  // ── EWB Settings ──
  getEWayBillSettings: async () => {
    const res = await getData(`api/v1/compliance/eway-bill/settings`);
    return res;
  },
  updateEWayBillSettings: async (payload: any) => {
    const res = await postData(`api/v1/compliance/eway-bill/settings`, payload);
    return res;
  },
  testEWayBillAuth: async (payload: { gstin?: string; username?: string; password?: string; clientId?: string; clientSecret?: string; environment?: string; email?: string }) => {
    const res = await postData(`api/v1/compliance/eway-bill/test-auth`, payload);
    return res;
  },
  testEInvoiceAuth: async (payload: { gstin?: string; username?: string; password?: string; clientId?: string; clientSecret?: string; environment?: string; email?: string }) => {
    const res = await postData(`api/v1/compliance/e-invoice/test-auth`, payload);
    return res;
  },
  // Looks up a GSTIN's live registration status (legal name, address, taxpayer type,
  // active/blocked) directly against NIC via Whitebooks — before generating an e-Invoice
  // for that party, so a bad GSTIN surfaces here instead of as a cryptic IRP rejection.
  lookupEInvoiceGstin: async (gstin: string): Promise<{ success: boolean; message?: string; errorCode?: string; authRequired?: boolean; data?: EInvoiceGstinDetails }> => {
    const res = await getData(`api/v1/compliance/e-invoice/gstin-details/${encodeURIComponent(gstin.trim().toUpperCase())}`);
    return res;
  },
  // Re-fetches a previously generated e-Invoice's live status/signed data straight from
  // NIC by IRN — confirms ACT vs CNL on NIC's side, and can recover the signed QR/invoice
  // JSON. NIC only serves this for IRNs generated within the last 72 hours.
  lookupEInvoiceIrn: async (
    irn: string,
    opts?: { irp?: string; supplierGstin?: string }
  ): Promise<{ success: boolean; message?: string; errorCode?: string; authRequired?: boolean; data?: EInvoiceIRNDetails }> => {
    const params = new URLSearchParams();
    if (opts?.irp) params.set('irp', opts.irp);
    if (opts?.supplierGstin) params.set('supplierGstin', opts.supplierGstin);
    const qs = params.toString();
    const res = await getData(`api/v1/compliance/e-invoice/irn-details/${encodeURIComponent(irn.trim())}${qs ? `?${qs}` : ''}`);
    return res;
  },
  // Verifies an HSN/SAC code against NIC's master via Whitebooks — catches a wrong or
  // too-short HSN on an item before it breaks e-Way Bill / e-Invoice generation later.
  verifyHsn: async (
    hsnCode: string
  ): Promise<{ success: boolean; message?: string; data?: { hsnCode: string; description: string | null } }> => {
    const res = await getData(`api/v1/compliance/eway-bill/hsn-details/${encodeURIComponent(hsnCode.trim())}`);
    return res;
  },

  // ── Source Documents Search ──
  searchInvoices: async (
    query?: string,
    warehouseId?: string,
    pendingEwbOnly: boolean = false,
    pendingEinvoiceOnly: boolean = false,
    b2bOnly: boolean = false,
  ) => {
    const params: Record<string, string> = { search: query || '', limit: '50', page: '1' };
    if (warehouseId && warehouseId !== 'ALL') params.warehouseId = warehouseId;
    if (pendingEwbOnly) params.pendingEwbOnly = 'true';
    if (pendingEinvoiceOnly) params.pendingEinvoiceOnly = 'true';
    if (b2bOnly) params.b2bOnly = 'true';
    const q = new URLSearchParams(params).toString();
    const res = await getData(`api/v1/sales-invoices?${q}`);
    return res;
  },
  getInvoiceById: async (id: string) => {
    const res = await getData(`api/v1/sales-invoices/${id}`);
    return res;
  },
  searchChallans: async (query?: string, warehouseId?: string) => {
    const params: Record<string, string> = { search: query || '', limit: '50', page: '1' };
    if (warehouseId && warehouseId !== 'ALL') params.warehouseId = warehouseId;
    const q = new URLSearchParams(params).toString();
    const res = await getData(`api/v1/challans?${q}`);
    return res;
  },
  getWarehouses: async () => {
    const res = await getData(`api/v1/warehouse`);
    return res;
  },

  // ── Company Settings & Turnover ──
  getCompanySettings: async () => {
    const res = await getData(`api/v1/company/settings`);
    return res;
  },
  updateCompanySettings: async (payload: {
    previousFyTurnover?: number;
    currentFyEstTurnover?: number;
    einvoiceManualOverride?: boolean;
    previous_fy_turnover?: number;
    current_fy_est_turnover?: number;
    einvoice_manual_override?: boolean;
  }) => {
    const res = await putData(`api/v1/company/settings`, payload);
    return res;
  },

  // ── E-Invoice Settings (QR & Print Customization) ──
  getEInvoiceSettings: async () => {
    const res = await getData(`api/v1/compliance/e-invoice/settings`);
    return res;
  },
  updateEInvoiceSettings: async (payload: {
    qrSize?: string;
    qrPosition?: string;
    showIrn?: boolean;
    showAckNumber?: boolean;
    showQrCode?: boolean;
    autoGenerate?: boolean;
    skipB2c?: boolean;
    alertOnFailure?: boolean;
    emailAlertsEnabled?: boolean;
    emailRecipients?: string;
    environment?: string;
    apiUsername?: string;
    apiPassword?: string;
    clientId?: string;
    clientSecret?: string;
    gstin?: string;
    apiEmail?: string;
    serverIp?: string;
  }) => {
    const res = await putData(`api/v1/compliance/e-invoice/settings`, payload);
    return res;
  },
};

export const complianceAPI = complianceApi;

